import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { toAbsoluteUrl } from '@/lib/helpers';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';

interface App {
  logo: string;
  title: string;
  description: string;
  url: string;
  color?: string;
}

export function AppsDrawer({ trigger }: { trigger: ReactNode }) {
  const [searchQuery, setSearchQuery] = useState('');

  const apps: App[] = [
    {
      logo: 'jira.svg',
      title: 'Jira',
      description: 'Project management',
      url: '/apps/jira',
      color: 'bg-blue-50',
    },
    {
      logo: 'inferno.svg',
      title: 'Inferno',
      description: 'Healthcare app',
      url: '/apps/inferno',
      color: 'bg-orange-50',
    },
    {
      logo: 'evernote.svg',
      title: 'Evernote',
      description: 'Notes management',
      url: '/apps/evernote',
      color: 'bg-green-50',
    },
    {
      logo: 'gitlab.svg',
      title: 'Gitlab',
      description: 'DevOps platform',
      url: '/apps/gitlab',
      color: 'bg-purple-50',
    },
    {
      logo: 'google-webdev.svg',
      title: 'Google Webdev',
      description: 'Web experiences',
      url: '/apps/google-webdev',
      color: 'bg-indigo-50',
    },
    {
      logo: 'telegram.svg',
      title: 'Telegram',
      description: 'Messaging',
      url: '/apps/telegram',
      color: 'bg-sky-50',
    },
    {
      logo: 'figma.svg',
      title: 'Figma',
      description: 'Design tool',
      url: '/apps/figma',
      color: 'bg-pink-50',
    },
    {
      logo: 'slack.svg',
      title: 'Slack',
      description: 'Team communication',
      url: '/apps/slack',
      color: 'bg-violet-50',
    },
    {
      logo: 'youtube.svg',
      title: 'YouTube',
      description: 'Video platform',
      url: '/apps/youtube',
      color: 'bg-red-50',
    },
  ];

  const filteredApps = apps.filter(
    (app) =>
      app.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Sheet>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="p-0 gap-0 sm:w-[480px] sm:max-w-none">
        <SheetHeader className="p-5 pb-4">
          <SheetTitle>Apps</SheetTitle>
        </SheetHeader>

        {/* Search Bar */}
        <div className="px-5 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search apps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Apps Grid */}
        <ScrollArea className="h-[calc(100vh-140px)]">
          <div className="p-5 pt-0">
            <div className="grid grid-cols-3 gap-4">
              {filteredApps.map((app) => (
                <Link
                  key={app.title}
                  to={app.url}
                  className="flex flex-col items-center gap-3 p-4 rounded-lg hover:bg-accent transition-colors group"
                >
                  <div
                    className={`flex items-center justify-center size-16 rounded-xl ${
                      app.color || 'bg-accent'
                    } group-hover:scale-110 transition-transform`}
                  >
                    <img
                      src={toAbsoluteUrl(`/media/brand-logos/${app.logo}`)}
                      className="size-10"
                      alt={app.title}
                    />
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <span className="text-sm font-semibold text-foreground group-hover:text-primary">
                      {app.title}
                    </span>
                    <span className="text-xs text-muted-foreground line-clamp-1">
                      {app.description}
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {filteredApps.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-sm text-muted-foreground">No apps found</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
